/**
 * One save control, read and redrawn.
 *
 * Apart from `save-word.ts`, which is the page and the presses on it, and apart
 * from `save-word-route.ts`, which is what the handler is asked. This is the
 * markup. Which elements a control is made of, what its button says in each
 * state, and how a press is posted where the route could not be reached.
 *
 * `src/user/save-word-markup.ts` and `partials/cidian-save-word.html` are what
 * write that markup, in a Lambda and in Hugo. The classes below are the whole
 * of the contract between the three.
 */

import { tagField, wordField } from "./save-word-route.js";

/** The button one control is pressed by. */
const buttonSelector = ".cb-save-word-action";

/** The line offering an account, `hidden` until a signed-out reader presses. */
const offerSelector = ".cb-save-word-offer";

/**
 * The word the button says, inside the button.
 *
 * **Relabelling writes here rather than on the button.** A 词典 entry draws its
 * control as a badge with a plus and a tick in it, and `button.textContent =`
 * would take both marks out along with the old word the first time a reader
 * pressed it. Both markups carry this element, so there is one thing to write
 * to whatever else the control holds.
 *
 * **The button itself is the fallback.** Both markups carry the element, and
 * what the fallback does is keep a control that has lost it working. A button
 * holding its word and nothing else is the shape both pages carried before the
 * element was added, and writing to it is right for them.
 */
const labelSelector = ".cb-save-word-label";

/**
 * What the button says in each of the two states.
 *
 * The same two words `savedWordActions` in `src/user/save-word-markup.ts`
 * writes. This bundle shares no module with the handler, being compiled by
 * `tsc -p src/site` for a browser, so the strings are written twice.
 * `save-word.test.ts` draws its fixture with `savedWordControl` and reads the
 * labels back, which is what fails when the two come apart.
 */
const actions = { save: "Save", held: "Saved" };

/**
 * The name the button carries for somebody who cannot see it.
 *
 * The visible word comes first, which is what a reader saying the name of a
 * control to their machine has to be able to say. WCAG 2.5.3 is the rule.
 */
function label(word: string, held: boolean): string {
  return held
    ? `${actions.held} ${word}, press to remove`
    : `${actions.save} ${word}`;
}

/** The button of one control, where it has one. */
export function buttonOf(control: HTMLElement): HTMLButtonElement | null {
  return control.querySelector<HTMLButtonElement>(buttonSelector);
}

/**
 * Whether the markup already says the reader holds this word.
 *
 * The attribute is what `savedWordControl` writes for a control drawn with
 * `held`, and what `drawSavedWord` below writes and takes off. Reading it here
 * keeps the one spelling of the word `cbSaved` in the file that owns it.
 */
export function drawnAsHeld(button: HTMLButtonElement): boolean {
  return button.dataset["cbSaved"] !== undefined;
}

/** Draw one button in the state the route reported. */
export function drawSavedWord(button: HTMLButtonElement, held: boolean): void {
  const said = button.querySelector<HTMLElement>(labelSelector) ?? button;
  const word = button.dataset["cbWordLabel"] ?? button.value;

  said.textContent = held ? actions.held : actions.save;
  button.setAttribute("aria-label", label(word, held));

  if (held) {
    button.dataset["cbSaved"] = "yes";
  } else {
    delete button.dataset["cbSaved"];
  }
}

/** Show the account offer under one control. */
export function revealAccountOffer(control: HTMLElement): void {
  const offer = control.querySelector<HTMLElement>(offerSelector);

  if (offer !== null) {
    offer.hidden = false;
  }
}

/**
 * Post the press the way the form would have.
 *
 * **The word is on the button and `submit()` posts no button**, so the value
 * moves to a hidden field first. Without that the fallback would post an empty
 * form and the reader's press would be lost, which is worse than the failure it
 * is recovering from.
 */
export function postWithoutScript(
  form: HTMLFormElement,
  word: string,
  tag?: string,
): void {
  const carry = (name: string, value: string): void => {
    const carried = form.ownerDocument.createElement("input");

    carried.type = "hidden";
    carried.name = name;
    carried.value = value;
    form.append(carried);
  };

  carry(wordField, word);
  if (tag !== undefined) {
    carry(tagField, tag);
  }
  form.submit();
}
